<template>
  <div ref="dateInputRef">
    <BaseInput
      type="text"
      icon="right"
      color="light"
      v-model="dateTimePickerValue"
      @keydown.escape="showDropdown = false"
      @input="formatInput"
      @keydown="handleKeyDown"
      @blur="onInputBlur"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
      ref="inputRef"
      class="w-full font-medium text-gray-600 bg-white rounded-lg cursor-pointer"
      :class="{ 'pointer-events-none': disabled }"
      :disabled="disabled"
      :error="formError ? formError : error"
      :showErrorAsTooltip="showErrorAsTooltip"
      :placeholder="
        showDatePicker && showTimePicker
          ? 'DD/MM/YYYY HH:MM'
          : showTimePicker && !showDatePicker
            ? 'HH:MM'
            : 'DD/MM/YYYY'
      ">
      <template #icon-right>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 pr-2 text-gray-500 w-7 cursor-pointer"
          :class="{ 'text-red-500': error }"
          viewBox="0 0 24 24"
          @click="toggleDropDown">
          <path
            fill="currentColor"
            d="M19 19H5V8h14m-3-7v2H8V1H6v2H5c-1.11 0-2 .89-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-1V1m-1 11h-5v5h5v-5Z" />
        </svg>
      </template>
    </BaseInput>
    <button
      v-if="datepickerValue && clearable"
      @click="onClear"
      class="absolute inset-y-0 flex items-center text-gray-300 transition-all outline-none cursor-pointer right-10 focus:outline-none hover:text-gray-600"
      :class="{ 'text-red-500': error }">
      <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mx-2 fill-current" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
      </svg>
    </button>
    <div
      ref="dateDropdownRef"
      class="bg-white rounded-lg shadow w-[300px]"
      v-if="showDropdown"
      :style="{
        position: strategy,
        top: y ? `${y}px` : '',
        left: x ? `${x}px` : '',
        zIndex: 9999,
      }">
      <div class="px-2.5 py-5 flex items-center gap-2.5" v-if="showDatePicker && showTimePicker">
        <BaseButton
          class="flex-1 h-8 text-base capitalize"
          :outline="!showDate"
          :class="{ 'bg-cyan-light text-theme-500 border-0 ': !showDate }"
          @click="showDate = true">
          {{ $t('date.date') }}</BaseButton
        >
        <BaseButton
          class="flex-1 h-8 text-base capitalize"
          :outline="showDate"
          :class="{ 'bg-cyan-light text-theme-500 border-0 ': showDate }"
          @click="showDate = false">
          {{ $t('date.time') }}</BaseButton
        >
      </div>
      <div class="px-4">
        <div v-if="!showYear" class="flex flex-col">
          <button
            type="button"
            class="inline-flex p-1 mx-auto transition duration-100 ease-in-out rounded-full cursor-pointer hover:bg-gray-200"
            :class="{
              'cursor-not-allowed opacity-25': year.toString() === years[0] && month === 0,
              'hidden': !showDate,
            }"
            :disabled="year.toString() === years[0] && month === 0"
            @click="onPreviousMonthClick">
            <svg
              class="inline-flex w-6 h-6 text-gray-500 rotate-90"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div class="flex items-center justify-between my-2">
            <div
              class="flex-1 text-base font-medium text-center text-gray-800"
              :class="{ 'cursor-pointer hover:text-theme-600': !showDate }"
              @click="onCurrentTimeClick">
              {{ showDate ? MONTHS[month] : time }}
              <span
                class="ml-1 cursor-pointer hover:text-blue-400"
                @click="openYearDropdown"
                v-if="showDate"
                >{{ year }}</span
              >
            </div>
            <button
              type="button"
              class="flex-1 text-base font-medium uppercase text-theme-600"
              @click="showDate ? initDate() : initTime()">
              {{ showDate ? $t('date.today') : $t('date.now') }}
            </button>
            <div></div>
          </div>

          <div class="flex flex-wrap mb-3 -mx-1" v-if="showDate">
            <div
              v-for="(day, index) in DAYS"
              :key="index"
              style="width: 14.26%"
              class="px-1 uppercase">
              <div class="text-sm font-light text-center text-slate-400">{{ day }}</div>
            </div>
          </div>
          <div v-else class="flex items-center text-sm font-light text-center text-slate-500">
            <div class="flex-1">Hour</div>
            <div class="flex-1">Minute</div>
          </div>
          <TimePicker v-if="!showDate" v-model="time" :recenter="recenterTime" />
          <div class="flex flex-wrap -mx-1" :class="{ 'hidden': !showDate }">
            <div
              v-for="(blankDay, index) in blankDays"
              :key="index"
              style="width: 14.28%"
              class="flex items-center justify-center px-1 py-1 m-1 mx-auto font-medium leading-none text-center pointer-events-none text-slate-200">
              {{ blankDay }}
            </div>
            <div
              v-for="(date, dateIndex) in no_of_days"
              :key="dateIndex"
              style="width: 14.28%"
              class="">
              <div
                @click="getDateValue(date)"
                class="flex items-center justify-center w-6 h-6 px-1 py-1 m-1 mx-auto font-medium leading-none text-center transition duration-100 ease-in-out border border-transparent rounded-md cursor-pointer"
                :class="{
                  'bg-cyan-light': isToday(date) && !isSelected(date),
                  'bg-cyan-base text-white': isSelected(date),
                }">
                {{ date }}
              </div>
            </div>
          </div>
          <button
            v-if="showDate"
            type="button"
            class="inline-flex p-1 mx-auto transition duration-100 ease-in-out rotate-90 rounded-full cursor-pointer hover:bg-gray-200"
            :class="{
              'cursor-not-allowed opacity-25':
                year.toString() === years[years.length - 1] && month === 11,
            }"
            :disabled="year.toString() === years[years.length - 1] && month === 11"
            @click="onNextMonthClick">
            <svg
              class="inline-flex w-6 h-6 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <!-- YEAR -->
        <div v-else>
          <div class="flex items-center justify-between mb-2">
            <div>
              <button
                type="button"
                class="inline-flex p-1 transition duration-100 ease-in-out rounded-full cursor-pointer hover:bg-gray-200"
                :class="{ 'cursor-not-allowed opacity-25': currentPage === 1 }"
                :disabled="currentPage === 1"
                @click="prevYearPage">
                <svg
                  class="inline-flex w-6 h-6 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
            <div>
              <span class="text-base font-bold text-gray-800"
                >{{ visibleYears[0] }} - {{ visibleYears[visibleYears.length - 1] }}</span
              >
            </div>
            <div>
              <button
                type="button"
                class="inline-flex p-1 transition duration-100 ease-in-out rounded-full cursor-pointer hover:bg-gray-200"
                :class="{ 'cursor-not-allowed opacity-25': currentPage === totalPages }"
                :disabled="currentPage === totalPages"
                @click="nextYearPage">
                <svg
                  class="inline-flex w-6 h-6 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div class="flex flex-wrap -mx-1">
            <div v-for="(year, index) in visibleYears" :key="index" class="w-1/4 mb-1">
              <div
                class="flex items-center justify-center p-6 m-1 mx-auto text-xl font-medium leading-none text-center transition duration-100 ease-in-out border border-transparent rounded-full cursor-pointer w-7 h-7 hover:text-theme-600"
                @click="onYearClick(year)">
                {{ year }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="px-2.5 py-2.5 flex items-center gap-2.5 bg-slate-50">
        <BaseButton outline class="flex-1 h-8 text-base capitalize border-0" @click="onClear">
          {{ $t('common.actions.clear') }}</BaseButton
        >
        <BaseButton
          class="flex-1 h-8 text-base capitalize"
          @click="
            {
              onSave();
              showDropdown = false;
            }
          ">
          {{ $t('common.actions.select') }}</BaseButton
        >
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="!!showErrorAsTooltip && tooltipVisible && (formError || error)"
        ref="tooltipRef"
        :style="{
          position: tooltipStrategy,
          top: tooltipY ? `${tooltipY}px` : '',
          left: tooltipX ? `${tooltipX}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-white bg-red-500 rounded-lg shadow-lg">
        <span class="text-xs truncate">{{ formError || error }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-red-500 fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watchEffect, readonly } from 'vue';
import { getCurrentTime, validateDatetime } from '@/utils/date';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { autoUpdate, flip, hide, offset, shift, useFloating } from '@floating-ui/vue';
import BaseInput from './BaseInput.vue';
import BaseButton from './BaseButton.vue';
import TimePicker from './TimePicker.vue';

const { t: $t } = useI18n();

type Props = {
  right?: boolean;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  showDatePicker?: boolean;
  showTimePicker?: boolean;
  formError?: string;
  startEmpty?: boolean;
  showErrorAsTooltip?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  showDatePicker: true,
  showTimePicker: true,
  showErrorAsTooltip: false,
});

const dateDropdownRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);

const error = ref('');
const isValidRef = ref(true);

const showDate = ref(props.showDatePicker);

// Tooltip positioning
const {
  x: tooltipX,
  y: tooltipY,
  strategy: tooltipStrategy,
  update: tooltipUpdate,
  middlewareData: tooltipMiddlewareData,
} = useFloating(inputRef, tooltipRef, {
  placement: 'top',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, tooltipUpdate) => {
    return autoUpdate(referenceEl, floatingEl, tooltipUpdate);
  },
});

function showTooltip() {
  tooltipVisible.value = true;
  tooltipUpdate();
}

function hideTooltip() {
  tooltipVisible.value = false;
}

watchEffect(() => {
  if (tooltipMiddlewareData.value.hide?.referenceHidden) {
    tooltipVisible.value = false;
  }
});

onClickOutside(dateDropdownRef, () => (showDropdown.value = false));

const MONTHS = [
  $t('date.months.0'),
  $t('date.months.1'),
  $t('date.months.2'),
  $t('date.months.3'),
  $t('date.months.4'),
  $t('date.months.5'),
  $t('date.months.6'),
  $t('date.months.7'),
  $t('date.months.8'),
  $t('date.months.9'),
  $t('date.months.10'),
  $t('date.months.11'),
];
const DAYS = [
  $t('date.days.0').substring(0, 2),
  $t('date.days.1').substring(0, 2),
  $t('date.days.2').substring(0, 2),
  $t('date.days.3').substring(0, 2),
  $t('date.days.4').substring(0, 2),
  $t('date.days.5').substring(0, 2),
  $t('date.days.6').substring(0, 2),
];

const years = ref([] as string[]);

const showDropdown = ref(false);

const datepickerValue = ref<string>('');

const dateTimePickerValue = defineModel<string>();

const time = ref('');

const recenterTime = ref(false);

function onCurrentTimeClick() {
  recenterTime.value = true;
  setTimeout(() => {
    recenterTime.value = false;
  }, 1000);
}

const day = ref(new Date().getDate());
const month = ref(new Date().getMonth());
const year = ref(new Date().getFullYear());

const showYear = ref(false);

const no_of_days = ref<number[]>([]);
const blankDays = ref<number[]>([]);

const date = ref<string | null>(null);

years.value = getYears();

function isToday(date: any) {
  const today = new Date();
  const d = new Date(year.value, month.value, date);

  return today.toLocaleDateString('en-GB') === d.toLocaleDateString('en-GB');
}

function isSelected(d: any) {
  const currentDate = new Date(year.value, month.value, d);
  return datepickerValue.value === currentDate.toLocaleDateString('en-GB');
}

onMounted(() => {
  if (dateTimePickerValue.value) {
    let [startDate, startTime] = dateTimePickerValue.value.split(' ');
    datepickerValue.value = startDate;
    time.value = startTime;
    year.value = parseInt(datepickerValue.value.split('/')[2]);
    month.value = parseInt(datepickerValue.value.split('/')[1]) - 1;
  } else {
    if (props.startEmpty) {
      dateTimePickerValue.value = '';
    } else {
      if (props.showDatePicker) initDate();
      if (props.showTimePicker) {
        if (time.value) dateTimePickerValue.value = `${datepickerValue.value} ${time.value}`;
        else initTime();
      } else {
        dateTimePickerValue.value = `${datepickerValue.value}`;
      }
    }
  }

  getNoOfDays();
});

function initDate() {
  let today = new Date();
  day.value = today.getDate();
  month.value = today.getMonth();
  year.value = today.getFullYear();
  datepickerValue.value = new Date(year.value, month.value, day.value).toLocaleDateString('en-GB');
}

function initTime() {
  time.value = getCurrentTime();
}

function getNoOfDays() {
  let daysInMonth = new Date(year.value, month.value + 1, 0).getDate();

  // find where to start calendar day of week
  let dayOfWeek = new Date(year.value, month.value).getDay();
  let daysInPrevMonth = new Date(year.value, month.value, 0).getDate();
  let blankdaysArray = [];
  for (let i = 1; i <= dayOfWeek; i++) {
    blankdaysArray.push(daysInPrevMonth - dayOfWeek + i);
  }

  let daysArray = [];
  for (let i = 1; i <= daysInMonth; i++) {
    daysArray.push(i);
  }

  blankDays.value = blankdaysArray;
  no_of_days.value = daysArray;
}

function getDateValue(d?: any) {
  if (d) day.value = d;
  let selectedDate = new Date(year.value, month.value, day.value);
  datepickerValue.value = selectedDate.toLocaleDateString('en-GB');

  date.value =
    selectedDate.getFullYear() +
    '-' +
    ('0' + selectedDate.getMonth()).slice(-2) +
    '-' +
    ('0' + selectedDate.getDate()).slice(-2);
}
// years
const yearsPerPage = 16;

function getCurrentYearPage() {
  const currentYear = new Date().getFullYear();
  const currentYearIndex = years.value.findIndex((y) => parseInt(y) === currentYear);
  if (currentYearIndex === -1) return 1;
  return Math.ceil((currentYearIndex + 1) / yearsPerPage);
}

const currentPage = ref(getCurrentYearPage());
const totalPages = Math.ceil(years.value.length / yearsPerPage);

const visibleYears = computed(() => {
  const startIndex = (currentPage.value - 1) * yearsPerPage;
  const endIndex = startIndex + yearsPerPage;
  return years.value.slice(startIndex, endIndex);
});

function prevYearPage() {
  currentPage.value--;
}
function nextYearPage() {
  currentPage.value++;
}

function openYearDropdown() {
  currentPage.value = getCurrentYearPage();
  showYear.value = true;
}

function getYears() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 20; i <= currentYear + 20; i++) {
    years.push(i.toString());
  }
  return years;
}

function onYearClick(selectedYear: string) {
  year.value = parseInt(selectedYear);
  showYear.value = false;
  getNoOfDays();
}

function onPreviousMonthClick() {
  month.value--;
  if (month.value === -1) {
    year.value = year.value - 1;
    month.value = 11;
  }
  getNoOfDays();
}

function onNextMonthClick() {
  month.value++;
  if (month.value === 12) {
    year.value = year.value + 1;
    month.value = 0;
  }
  getNoOfDays();
}

function onClear() {
  dateTimePickerValue.value = '';
  showDropdown.value = false;
}

function onSave() {
  error.value = '';
  isValidRef.value = true;
  dateTimePickerValue.value = `${datepickerValue.value}${time.value ? ' ' + time.value : ''}`;
  emitValidationState(true, '');
}

const inputLength = ref(16);
if (props.showDatePicker && !props.showTimePicker) inputLength.value = 10;
else if (!props.showDatePicker && props.showTimePicker) inputLength.value = 5;
const isDeleting = ref(false);

function formatInput(event: Event) {
  if (!event.target) {
    return;
  }
  const target = event.target as HTMLInputElement;
  let value = target.value;
  if (value.length === 0) {
    dateTimePickerValue.value = undefined;
    return;
  }
  const previousValue = dateTimePickerValue.value || '';

  const isDeletion = value.length < previousValue.length;

  if (isDeletion) {
    // For deletion, just update the value directly without any processing
    dateTimePickerValue.value = value;
    error.value = '';
    isDeleting.value = false;

    // If completely cleared, reset dateTimePickerValue to empty
    if (value.length === 0) {
      dateTimePickerValue.value = '';
      emitValidationState(true, '');
    } else {
      // For partial deletions, emit validation state
      emitValidationState(!error.value, error.value);
    }
    return;
  }

  value = value.replace(/[^\d/: ]/g, '');

  if (props.showDatePicker) {
    // Format date part (DD/MM/YYYY)
    let datePart = value;
    let timePart = '';

    if (props.showTimePicker && value.includes(' ')) {
      const parts = value.split(' ');
      datePart = parts[0];
      timePart = parts[1] || '';
    }

    const digitsOnly = datePart.replace(/\D/g, '');

    if (digitsOnly.length >= 2) {
      datePart = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2);
    } else {
      datePart = digitsOnly;
    }
    if (digitsOnly.length >= 4) {
      datePart =
        digitsOnly.substring(0, 2) +
        '/' +
        digitsOnly.substring(2, 4) +
        '/' +
        digitsOnly.substring(4);
    }
    if (digitsOnly.length > 8) {
      datePart =
        digitsOnly.substring(0, 2) +
        '/' +
        digitsOnly.substring(2, 4) +
        '/' +
        digitsOnly.substring(4, 8);
    }

    if (props.showTimePicker) {
      if (digitsOnly.length > 8) {
        const timeDigits = digitsOnly.substring(8);
        if (timeDigits.length >= 2) {
          timePart = timeDigits.substring(0, 2) + ':' + timeDigits.substring(2);
        } else {
          timePart = timeDigits;
        }
        if (timeDigits.length > 4) {
          timePart = timeDigits.substring(0, 2) + ':' + timeDigits.substring(2, 4);
        }
      }

      if (value.includes(' ')) {
        const existingTimePart = value.split(' ')[1] || '';
        const timeDigits = existingTimePart.replace(/\D/g, '');

        if (timeDigits.length >= 2) {
          timePart = timeDigits.substring(0, 2) + ':' + timeDigits.substring(2);
        } else {
          timePart = timeDigits;
        }
        if (timeDigits.length > 4) {
          timePart = timeDigits.substring(0, 2) + ':' + timeDigits.substring(2, 4);
        }
      }
    }

    value = datePart;

    if (props.showTimePicker && timePart) {
      value = datePart + ' ' + timePart;
    }
  } else if (props.showTimePicker) {
    // Format time only (HH:MM)
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length >= 2) {
      value = digitsOnly.substring(0, 2) + ':' + digitsOnly.substring(2);
    } else {
      value = digitsOnly;
    }
    if (digitsOnly.length > 4) {
      value = digitsOnly.substring(0, 2) + ':' + digitsOnly.substring(2, 4);
    }
  }

  // Limit to expected length
  if (value.length > inputLength.value) {
    value = value.slice(0, inputLength.value);
  }

  // Enhanced validation during typing
  let isValid = true;
  let validationError = '';

  if (value.length > 0) {
    if (props.showDatePicker) {
      const parts = value.split(/[/ ]/);
      const datePart = parts[0];

      // Validate date parts as they're being typed
      if (datePart.includes('/')) {
        const dateParts = datePart.split('/');

        // Validate day
        if (dateParts[0] && dateParts[0].length >= 1) {
          const day = Number(dateParts[0]);
          if (day === 0 || (dateParts[0].length === 2 && day > 31)) {
            isValid = false;
            validationError = $t('date.invalidDate');
          }
        }

        // Validate month
        if (dateParts[1] && dateParts[1].length >= 1) {
          const month = Number(dateParts[1]);
          if (month === 0 || (dateParts[1].length === 2 && month > 12)) {
            isValid = false;
            validationError = $t('date.invalidDate');
          }
        }

        // Validate complete date if all parts are present
        if (dateParts.length === 3 && dateParts[2] && dateParts[2].length === 4) {
          const day = Number(dateParts[0]);
          const month = Number(dateParts[1]);
          const year = Number(dateParts[2]);

          // Check if it's a valid date
          const testDate = new Date(year, month - 1, day);
          if (
            testDate.getDate() !== day ||
            testDate.getMonth() !== month - 1 ||
            testDate.getFullYear() !== year
          ) {
            isValid = false;
            validationError = $t('date.invalidDate');
          }
        }
      }

      // Validate time part if present (for date+time combination)
      if (isValid && props.showTimePicker && value.includes(' ')) {
        const timePart = value.split(' ')[1];
        if (timePart && timePart.includes(':')) {
          const timeParts = timePart.split(':');
          if (timeParts[0] && Number(timeParts[0]) > 23) {
            isValid = false;
            validationError = $t('date.invalidDate');
          }
          if (timeParts[1] && Number(timeParts[1]) > 59) {
            isValid = false;
            validationError = $t('date.invalidDate');
          }
        }
      }

      // Use existing validateDatetime for complete validation
      if (isValid && !validateDatetime(value) && value.length >= 8) {
        isValid = false;
        validationError = $t('date.invalidDate');
      }
    } else if (props.showTimePicker) {
      // Validate time format (time-only mode)
      if (value.includes(':')) {
        const timeParts = value.split(':');
        if (timeParts[0] && Number(timeParts[0]) > 23) {
          isValid = false;
          validationError = $t('date.invalidDate');
        }
        if (timeParts[1] && Number(timeParts[1]) > 59) {
          isValid = false;
          validationError = $t('date.invalidDate');
        }
      }
    }
  }

  // Update error state
  error.value = validationError;
  isValidRef.value = isValid;

  // Update internal state only if valid or still typing
  if (isValid || value.length < 8) {
    if (validateDatetime(value)) {
      const parts = value.split(/[/ ]/);
      if (props.showDatePicker && parts.length >= 3) {
        month.value = Number(parts[1]) - 1;
        year.value = Number(parts[2]);
        getDateValue(Number(parts[0]));
        if (props.showTimePicker && parts[3]) {
          time.value = parts[3];
        }
        getNoOfDays();
      } else if (!props.showDatePicker && props.showTimePicker) {
        time.value = value;
      }
    }
  }

  dateTimePickerValue.value = value;

  // Emit validation state to parent components
  emitValidationState(isValid, validationError);
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Backspace' || event.key === 'Delete') {
    isDeleting.value = true;
  }

  const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
    'Escape',
    'Enter',
  ];

  const isNumber = event.key >= '0' && event.key <= '9';
  const isAllowedKey = allowedKeys.includes(event.key);
  const isCtrlKey = event.ctrlKey || event.metaKey; // Allow Ctrl+A, Ctrl+C, etc.

  if (!isNumber && !isAllowedKey && !isCtrlKey) {
    event.preventDefault();
  }
}

const emit = defineEmits(['on-blur', 'validation-change']);
function onInputBlur() {
  emit('on-blur');
}

function emitValidationState(validationIsValid: boolean, errorMessage: string = '') {
  isValidRef.value = validationIsValid;
  emit('validation-change', { isValid: validationIsValid, error: errorMessage });
}

// Expose validation state to parent components
defineExpose({
  isValid: readonly(isValidRef),
  getValidationState: () => ({ isValid: isValidRef.value, error: error.value }),
});

const dateInputRef = ref<HTMLElement | null>(null);
const { x, y, strategy, middlewareData, update } = useFloating(dateInputRef, dateDropdownRef, {
  placement: 'bottom-start',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, update) => {
    return autoUpdate(referenceEl, floatingEl, update);
  },
});

watchEffect(() => {
  if (middlewareData.value.hide?.referenceHidden) {
    showDropdown.value = false;
  }
});

function toggleDropDown() {
  showDropdown.value = !showDropdown.value;
  if (showDropdown.value) {
    update();
  }
}
</script>
